import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheckCircle,
  faEdit,
  faSpinner,
  faExclamationCircle,
  faChevronDown,
  faChevronRight,
  faPlay,
  faSearch,
  faFileExcel,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import { useCustomLoading } from "../hooks/useCustomLoading";
import RepairProcessTable from "../components/RepairProcessTable";
import PanelBayPaintingModal from "../components/PanelBayPaintingModal";
import ProjectCompletionModal from "../components/ProjectCompletionModal";
import InlineFieldTooltip from "../components/InlineFieldTooltip";
import { exportRepairProjectToExcel } from "../utils/excelExport";
import { exportRepairProjectToPDF } from "../utils/pdfExport";
import {
  getAreaDataLevelLabels,
  getBridgeLevelLabels,
} from "../utils/bridgeLevelLabels";

/**
 * RepairProjectDetails Page
 *
 * Enhanced view showing:
 * - Project information
 * - All spans with inspection data
 * - Detailed repair processes (similar to simulation results)
 * - Cost estimates with breakdown
 * - Execution status
 */
const RepairProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useCustomLoading();

  const [project, setProject] = useState(null);
  const [executionDetails, setExecutionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCostBreakdownCollapsed, setIsCostBreakdownCollapsed] =
    useState(false);
  const [isCostBreakdownSticky, setIsCostBreakdownSticky] = useState(false);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const [costBreakdownExpandedHeight, setCostBreakdownExpandedHeight] =
    useState(0);
  const [costBreakdownPinnedHeight, setCostBreakdownPinnedHeight] =
    useState(0);
  const [isSpansCollapsed, setIsSpansCollapsed] = useState(false);
  const [spansSearchTerm, setSpansSearchTerm] = useState("");
  const [spansFilterStatus, setSpansFilterStatus] = useState("all");
  const [activePanelBaySpan, setActivePanelBaySpan] = useState(null);
  const [panelBayModalMode, setPanelBayModalMode] = useState("save");
  const [bridgeLevelLabels, setBridgeLevelLabels] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [success, setSuccess] = useState(null);
  const [startingProject, setStartingProject] = useState(false);
  const [completionActionElementId, setCompletionActionElementId] =
    useState(null);
  const topStickySectionRef = useRef(null);
  const costBreakdownSentinelRef = useRef(null);
  const costBreakdownStickyStateRef = useRef(false);
  const costBreakdownCardRef = useRef(null);
  const repairDetailsSectionRef = useRef(null);
  const lastCostBreakdownScrollTopRef = useRef(0);

  const fetchAllDetails = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) {
          setLoading(true);
        }
        setError(null);

        // Fetch both project details and execution details
        const [projectResponse, executionResponse] = await Promise.all([
          api.get(`/repair-projects/${id}`),
          api.get(`/repair-projects/${id}/execution`),
        ]);

        if (projectResponse.data.success) {
          const projectData = projectResponse.data.data;
          setProject(projectData);

          // Fetch bridge level configuration if bridge_id is available
          if (projectData.bridge_id) {
            try {
              const bridgeResponse = await api.get(
                `/bridges/${projectData.bridge_id}`,
              );
              if (bridgeResponse.data.success) {
                setBridgeLevelLabels(
                  bridgeResponse.data.data.levelLabels || null,
                );
              }
            } catch (err) {
              console.warn("Could not fetch bridge level labels:", err);
            }
          }
        }

        if (executionResponse.data.success) {
          setExecutionDetails(executionResponse.data.data);
        }
      } catch (err) {
        console.error("Error fetching details:", err);
        setError(
          err.response?.data?.message || "Failed to fetch project details",
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [id],
  );

  useEffect(() => {
    if (id) {
      fetchAllDetails();
    }
  }, [id, fetchAllDetails]);

  useEffect(() => {
    costBreakdownStickyStateRef.current = isCostBreakdownSticky;
  }, [isCostBreakdownSticky]);

  useEffect(() => {
    const stickyNode = topStickySectionRef.current;
    if (!stickyNode) {
      return undefined;
    }

    let frameId = null;
    const updateHeight = () => {
      frameId = null;
      setStickyHeaderHeight(stickyNode.offsetHeight || 0);
    };
    const scheduleUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updateHeight);
    };

    scheduleUpdate();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null;

    resizeObserver?.observe(stickyNode);
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [loading, error, project, executionDetails]);

  useLayoutEffect(() => {
    const cardNode = costBreakdownCardRef.current;
    if (!cardNode) {
      return undefined;
    }

    let frameId = null;
    const updateHeight = () => {
      frameId = null;

      const nextHeight = cardNode.offsetHeight || 0;

      if (isCostBreakdownCollapsed || isCostBreakdownSticky) {
        setCostBreakdownPinnedHeight((currentHeight) =>
          Math.abs(currentHeight - nextHeight) > 1
            ? nextHeight
            : currentHeight,
        );
        return;
      }

      setCostBreakdownExpandedHeight((currentHeight) =>
        Math.abs(currentHeight - nextHeight) > 1 ? nextHeight : currentHeight,
      );
    };
    const scheduleUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updateHeight);
    };

    updateHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null;

    resizeObserver?.observe(cardNode);
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [
    loading,
    error,
    project,
    executionDetails,
    isCostBreakdownCollapsed,
    isCostBreakdownSticky,
  ]);

  useEffect(() => {
    const sentinelNode = costBreakdownSentinelRef.current;
    if (!sentinelNode || loading || error || !project) {
      costBreakdownStickyStateRef.current = false;
      setIsCostBreakdownSticky(false);
      return undefined;
    }

    const scrollContainer = sentinelNode.closest("main");
    if (!scrollContainer) {
      return undefined;
    }

    const isCompletedProject = project.status === "completed";
    const collapseThreshold = stickyHeaderHeight + 8;
    const expandHysteresis = isCompletedProject ? 36 : 44;
    let frameId = null;
    let collapseScrollTop = 0;
    let expandScrollTop = 0;
    let allowImmediateStateSync = true;

    const updateTriggerPoints = () => {
      const scrollTop = scrollContainer.scrollTop;
      const rootRect = scrollContainer.getBoundingClientRect();
      const sentinelRect = sentinelNode.getBoundingClientRect();
      const sentinelContentTop = sentinelRect.top - rootRect.top + scrollTop;

      collapseScrollTop = Math.max(0, sentinelContentTop - collapseThreshold);
      expandScrollTop = Math.max(0, collapseScrollTop - expandHysteresis);
    };

    const syncStickyState = (nextStickyState) => {
      if (costBreakdownStickyStateRef.current === nextStickyState) {
        return;
      }

      costBreakdownStickyStateRef.current = nextStickyState;
      setIsCostBreakdownSticky(nextStickyState);
    };

    const evaluateStickyState = () => {
      frameId = null;

      const scrollTop = scrollContainer.scrollTop;
      const previousScrollTop = lastCostBreakdownScrollTopRef.current;
      const isScrollingDown = scrollTop > previousScrollTop + 1;
      const isScrollingUp = scrollTop < previousScrollTop - 1;
      const shouldSyncImmediately = allowImmediateStateSync;

      allowImmediateStateSync = false;
      lastCostBreakdownScrollTopRef.current = scrollTop;

      if (!costBreakdownStickyStateRef.current) {
        if (
          scrollTop >= collapseScrollTop &&
          (shouldSyncImmediately || isScrollingDown)
        ) {
          syncStickyState(true);
        }
        return;
      }

      if (
        scrollTop <= expandScrollTop &&
        (shouldSyncImmediately || isScrollingUp)
      ) {
        syncStickyState(false);
      }
    };

    const scheduleEvaluation = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(evaluateStickyState);
    };

    lastCostBreakdownScrollTopRef.current = scrollContainer.scrollTop;
    updateTriggerPoints();
    evaluateStickyState();

    const handleResize = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        updateTriggerPoints();
        allowImmediateStateSync = true;
        lastCostBreakdownScrollTopRef.current = scrollContainer.scrollTop;
        evaluateStickyState();
      });
    };

    scrollContainer.addEventListener("scroll", scheduleEvaluation, {
      passive: true,
    });
    window.addEventListener("resize", handleResize);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      scrollContainer.removeEventListener("scroll", scheduleEvaluation);
      window.removeEventListener("resize", handleResize);
    };
  }, [stickyHeaderHeight, loading, error, project]);

  const areaDataLabels = useMemo(
    () => getAreaDataLevelLabels(bridgeLevelLabels, "Element"),
    [bridgeLevelLabels],
  );
  const childElementLabels = useMemo(
    () =>
      getBridgeLevelLabels(
        bridgeLevelLabels,
        project?.child_level_number,
        "Child Element",
      ),
    [bridgeLevelLabels, project?.child_level_number],
  );
  const parentGroupLabels = useMemo(() => {
    const areaLevel = Number(project?.area_data_level_number);
    if (!Number.isInteger(areaLevel) || areaLevel <= 1) {
      return null;
    }

    return getBridgeLevelLabels(bridgeLevelLabels, areaLevel - 1, "Parent");
  }, [bridgeLevelLabels, project?.area_data_level_number]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const handleClosePanelBayModal = () => {
    setActivePanelBaySpan(null);
    setPanelBayModalMode("save");
  };

  const handleOpenPanelBayModal = (element, mode = "save") => {
    setActivePanelBaySpan(element);
    setPanelBayModalMode(mode);
    setError(null);
  };

  const handleStartProject = async () => {
    try {
      setStartingProject(true);
      setError(null);
      setSuccess(null);

      const response = await api.post(`/repair-projects/${id}/start`);

      if (response.data.success) {
        await fetchAllDetails({ silent: true });
        setIsSpansCollapsed(false);
        setSuccess(
          `Project started. Mark each ${areaDataLabels.singular.toLowerCase()} as completed or undamaged to advance progress.`,
        );
        window.setTimeout(() => {
          setSuccess(null);
        }, 4000);
        requestAnimationFrame(() => {
          repairDetailsSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    } catch (err) {
      console.error("Error starting project:", err);
      setError(err.response?.data?.message || "Failed to start project");
    } finally {
      setStartingProject(false);
    }
  };

  const handlePanelBaySave = async (
    panelBayUpdates,
    actionMode = panelBayModalMode,
  ) => {
    if (!activePanelBaySpan) {
      return;
    }
    const updates = Array.isArray(panelBayUpdates)
      ? panelBayUpdates
      : [panelBayUpdates];
    const validUpdates = updates.filter((update) =>
      Number.isFinite(Number(update?.elementId)),
    );

    if (validUpdates.length === 0) {
      return;
    }

    const shouldMarkCompleted = actionMode === "complete";
    const shouldMarkUndamaged = actionMode === "undamaged";
    const completionElementId = Number(
      activePanelBaySpan?.id || activePanelBaySpan?.element_id,
    );

    try {
      setError(null);
      setSuccess(null);
      await Promise.all(
        validUpdates.map((update) =>
          api.put(
            `/repair-projects/${id}/spans/${Number(update.elementId)}/panel-bays`,
            {
              actualPaintedArea: update.actualPaintedArea,
              actualPaintedAreaPercent: update.actualPaintedAreaPercent,
            },
          ),
        ),
      );

      if (
        (shouldMarkCompleted || shouldMarkUndamaged) &&
        Number.isFinite(completionElementId)
      ) {
        setCompletionActionElementId(completionElementId);
        await api.put(
          `/repair-projects/${id}/elements/${completionElementId}/completion`,
          {
            action: shouldMarkUndamaged ? "undamaged" : "completed",
          },
        );
      }

      setError(null);
      await fetchAllDetails({ silent: true });
      handleClosePanelBayModal();
    } catch (err) {
      console.error("Error updating painted area:", err);
      const message =
        err.response?.data?.message ||
        (shouldMarkCompleted || shouldMarkUndamaged
          ? "Failed to mark element as completed"
          : "Failed to update painted area");
      setError(message);
      throw new Error(message);
    } finally {
      if (shouldMarkCompleted || shouldMarkUndamaged) {
        setCompletionActionElementId(null);
      }
    }
  };

  const handleElementCompletion = async (element, action) => {
    const elementId = Number(element?.id || element?.element_id);
    if (!Number.isFinite(elementId)) {
      return;
    }

    try {
      setCompletionActionElementId(elementId);
      setError(null);
      setSuccess(null);

      await api.put(`/repair-projects/${id}/elements/${elementId}/completion`, {
        action,
      });

      await fetchAllDetails({ silent: true });
    } catch (err) {
      console.error("Error updating element completion:", err);
      setError(
        err.response?.data?.message || "Failed to update element completion",
      );
    } finally {
      setCompletionActionElementId(null);
    }
  };

  const handleProjectCompletion = async (completionData) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await api.post(
        `/repair-projects/${id}/complete`,
        completionData,
      );

      if (response.data.success) {
        await fetchAllDetails();
        setSuccess(
          `Project completed successfully. Baseline data for all ${areaDataLabels.plural.toLowerCase()} has been updated.`,
        );
        window.setTimeout(() => {
          setSuccess(null);
        }, 4000);
      }
    } catch (err) {
      console.error("Error completing project:", err);
      const message =
        err.response?.data?.message || "Failed to complete project";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="text-4xl text-blue-500 mb-4"
          />
          <p className="text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="text-red-600 mr-3"
            />
            <p className="text-red-800">{error || "Project not found"}</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/repair-projects")}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Projects
        </button>
      </div>
    );
  }

  const isCompleted = project.status === "completed";
  const hasStarted = Boolean(project.actual_start_date);
  const canEditProject = !isCompleted;

  // Use elements if available, fallback to spans for backward compatibility
  const elements = executionDetails?.elements || executionDetails?.spans || [];
  const totalElements =
    executionDetails?.total_elements || executionDetails?.total_spans || 0;
  const completedElements =
    executionDetails?.completed_elements ||
    executionDetails?.completed_spans ||
    0;
  const progressPercentage = Number(executionDetails?.progress_percentage || 0);
  const getAreaDataCountLabel = (count) =>
    (count === 1
      ? areaDataLabels.singular
      : areaDataLabels.plural
    ).toLowerCase();
  const completionProgressLabel = `${progressPercentage}% Complete (${completedElements} of ${totalElements} ${getAreaDataCountLabel(totalElements)})`;
  const canStartProject = !isCompleted && !hasStarted;
  const canShowCompleteProject = !isCompleted && hasStarted;
  const canFinalizeProject =
    canShowCompleteProject && progressPercentage === 100;

  const totalEstimatedCost =
    elements.reduce(
      (sum, element) => sum + (parseFloat(element.estimated_cost) || 0),
      0,
    ) || 0;
  const estimatedSummary = executionDetails?.estimated_summary || {};
  const displayedEstimatedTotal = Number(
    estimatedSummary.estimatedTotalCost || totalEstimatedCost || 0,
  );
  const actualCostValue =
    project.actual_cost !== null &&
    project.actual_cost !== undefined &&
    project.actual_cost !== ""
      ? Number(project.actual_cost)
      : null;
  const hasActualCostSummary = isCompleted || actualCostValue !== null;
  const actualCostVariance =
    actualCostValue !== null ? actualCostValue - displayedEstimatedTotal : null;
  const actualCostVarianceDisplay =
    actualCostVariance === null
      ? "Not recorded"
      : `${actualCostVariance > 0 ? "+" : ""}${formatCurrency(actualCostVariance)}`;
  const actualCostVarianceTone =
    actualCostVariance === null
      ? "text-slate-500"
      : actualCostVariance > 0.01
        ? "text-red-600"
        : actualCostVariance < -0.01
          ? "text-emerald-600"
          : "text-blue-700";
  const isCostBreakdownOpen =
    !isCostBreakdownCollapsed && !isCostBreakdownSticky;
  const isCostBreakdownPinned = isCostBreakdownSticky;
  const shouldPreserveCostBreakdownSpace =
    isCostBreakdownPinned &&
    !isCostBreakdownCollapsed &&
    costBreakdownExpandedHeight > 0;
  const costBreakdownSpacerHeight =
    shouldPreserveCostBreakdownSpace && costBreakdownPinnedHeight > 0
      ? Math.max(0, costBreakdownExpandedHeight - costBreakdownPinnedHeight)
      : 0;

  // Check for inconsistent completion state
  const hasIncompleteElements =
    isCompleted && executionDetails && completedElements < totalElements;
  const hasNoCompletionData =
    isCompleted &&
    (!project.actual_end_date ||
      !project.actual_cost ||
      parseFloat(project.actual_cost) === 0);

  // Filter elements based on search and status
  const filteredElements = elements.filter((element) => {
    // Use new field names with fallback to old ones
    const elementCode = element.element_code || element.span_code || "";
    const elementName = element.element_name || element.span_name || "";
    const parentName = element.parent_name || element.frame_name || "";
    const elementStatus =
      element.element_status || element.span_status || "pending";

    // Search filter
    const matchesSearch =
      !spansSearchTerm ||
      elementCode.toLowerCase().includes(spansSearchTerm.toLowerCase()) ||
      elementName.toLowerCase().includes(spansSearchTerm.toLowerCase()) ||
      parentName.toLowerCase().includes(spansSearchTerm.toLowerCase());

    // Status filter
    const matchesStatus =
      spansFilterStatus === "all" || elementStatus === spansFilterStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div
        ref={topStickySectionRef}
        className="sticky top-0 z-20 -mx-4 mb-6 border-b border-slate-200 bg-gray-100/95 px-4 pb-4 pt-1 backdrop-blur supports-[backdrop-filter]:bg-gray-100/85 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
      >
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/repair-projects")}
            className="text-blue-600 hover:text-blue-800 mb-2 text-sm"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back to Projects
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h1 className="text-2xl font-bold text-gray-900">
                  {project.project_name}
                </h1>
                <p className="text-sm text-gray-500">
                  Created by {project.created_by_username || "N/A"}, at{" "}
                  {project.created_at
                    ? new Date(project.created_at).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              <p className="text-sm text-gray-600">
                {project.bridge_name} • {totalElements}{" "}
                {getAreaDataCountLabel(totalElements)}
              </p>
            </div>

            <div className="flex items-center space-x-3">
              {canEditProject && (
                <button
                  onClick={() => navigate(`/repair-projects/${id}/edit`)}
                  className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                  title="Edit Project"
                >
                  <FontAwesomeIcon icon={faEdit} className="mr-2" />
                  Edit Project
                </button>
              )}
              <button
                onClick={() => {
                  const projectData = {
                    ...project,
                    elements: elements,
                    spans: elements,
                    levelLabels: bridgeLevelLabels,
                    estimatedSummary,
                    displayedEstimatedTotal,
                  };
                  exportRepairProjectToExcel(projectData);
                }}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                title="Export to Excel"
              >
                <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                Excel
              </button>
              <button
                onClick={() => {
                  const projectData = {
                    ...project,
                    elements: elements,
                    spans: elements,
                    levelLabels: bridgeLevelLabels,
                    estimatedSummary,
                    displayedEstimatedTotal,
                  };
                  exportRepairProjectToPDF(projectData);
                }}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                title="Export to PDF"
              >
                <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                PDF
              </button>
              {canStartProject && (
                <button
                  onClick={handleStartProject}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  title="Start project on this page"
                  disabled={startingProject}
                >
                  <FontAwesomeIcon icon={faPlay} className="mr-2" />
                  Start Project
                </button>
              )}
              {canShowCompleteProject && (
                <button
                  onClick={() => setShowCompletionModal(true)}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={!canFinalizeProject}
                  title={
                    canFinalizeProject
                      ? "Complete project"
                      : `Mark all ${areaDataLabels.plural.toLowerCase()} completed or undamaged first`
                  }
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                  Complete Project
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex flex-wrap items-center gap-2 gap-y-3">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}
          >
            {project.status?.toUpperCase()}
          </span>
          {project.priority && (
            <span
              className={`ml-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                project.priority === "critical"
                  ? "bg-red-100 text-red-800"
                  : project.priority === "high"
                    ? "bg-orange-100 text-orange-800"
                    : project.priority === "medium"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800"
              }`}
            >
              {project.priority?.toUpperCase()} PRIORITY
            </span>
          )}
          {executionDetails?.progress_percentage !== undefined &&
            executionDetails?.progress_percentage !== null && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {completionProgressLabel}
              </span>
            )}
          <div className="ml-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-600">
            <span>
              Start Date:{" "}
              <span className="font-medium text-gray-900">
                {project.actual_start_date ||
                  project.planned_start_date ||
                  "Not set"}
              </span>
            </span>
            <span>
              Duration:{" "}
              <span className="font-medium text-gray-900">
                {project.duration_weeks
                  ? `${project.duration_weeks} weeks`
                  : "TBD"}
              </span>{" "}
              <span className="text-gray-500">
                (Weeks limit:{" "}
                <span className="font-medium text-gray-900">
                  {project.weeks_limit || estimatedSummary.weekLimit || "N/A"}
                </span>
                )
              </span>
            </span>
            <span>
              Derived Teams:{" "}
              <span className="font-medium text-gray-900">
                {estimatedSummary.teamsRequired || project.crews_count || 1}
              </span>
            </span>
          </div>
        </div>
      </div>

      {success && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
          {success}
        </div>
      )}

      {/* Warning for Inconsistent Completion State */}
      {(hasIncompleteElements || hasNoCompletionData) && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="text-yellow-600 mt-0.5 mr-3"
            />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-800">
                Incomplete Project Data
              </h4>
              <p className="text-sm text-yellow-700 mt-1">
                This project is marked as "completed" but has inconsistent data:
              </p>
              <ul className="list-disc list-inside text-sm text-yellow-700 mt-2 space-y-1">
                {hasIncompleteElements && (
                  <li>
                    Only {completedElements} of {totalElements}{" "}
                    {getAreaDataCountLabel(totalElements)} are marked as
                    complete ({executionDetails.progress_percentage}% progress)
                  </li>
                )}
                {hasNoCompletionData && (
                  <li>Missing completion date or actual costs</li>
                )}
              </ul>
              <p className="text-sm text-yellow-700 mt-2">
                <strong>Solution:</strong> This likely happened because the
                project status was manually changed. To fix this, change the
                status back to "pending" and use the proper "Complete Project"
                workflow which will mark all
                {` ${areaDataLabels.plural.toLowerCase()} `}
                as complete and record actual costs.
              </p>
            </div>
          </div>
        </div>
      )}

      <>
        <div ref={costBreakdownSentinelRef} className="h-0" />
        <div className="mb-6">
          <div
            ref={costBreakdownCardRef}
            className={`sticky z-[15] overflow-hidden rounded-xl border border-gray-200 bg-white transition-shadow ${
              isCostBreakdownPinned ? "shadow-md" : "shadow-sm"
            }`}
            style={{ top: `${stickyHeaderHeight}px` }}
          >
            <button
              type="button"
              onClick={() => {
                if (!isCostBreakdownSticky) {
                  setIsCostBreakdownCollapsed(!isCostBreakdownCollapsed);
                }
              }}
              className={`w-full text-left ${
                isCostBreakdownPinned ? "px-4 py-3" : "px-5 py-4"
              }`}
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={isCostBreakdownOpen ? faChevronDown : faChevronRight}
                    className="w-4 h-4 text-gray-500"
                  />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Estimated Cost Breakdown
                    </h2>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-700">
                    Process:{" "}
                    {formatCurrency(
                      estimatedSummary.repairProcessCostTotal || 0,
                    )}
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-700">
                    Mobilization:{" "}
                    {formatCurrency(
                      estimatedSummary.estimatedMobilizationCost || 0,
                    )}
                  </span>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-semibold text-blue-700">
                    Estimated Total: {formatCurrency(displayedEstimatedTotal)}
                  </span>
                  {hasActualCostSummary && actualCostValue !== null && (
                    <>
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
                        Actual Total: {formatCurrency(actualCostValue)}
                      </span>
                      <span
                        className={`rounded-full border px-3 py-1 font-semibold ${
                          actualCostVariance > 0.01
                            ? "border-red-200 bg-red-50 text-red-700"
                            : actualCostVariance < -0.01
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-blue-200 bg-blue-50 text-blue-700"
                        }`}
                      >
                        Variance: {actualCostVarianceDisplay}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </button>

            {isCostBreakdownOpen && (
              <div className="border-t border-gray-200 px-5 pb-5 pt-4">
                <div className="grid gap-4 lg:grid-cols-[minmax(280px,1.15fr)_minmax(0,1fr)]">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Process Components
                        </p>
                        <p className="text-sm text-slate-600">
                          Direct repair work costs only
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Process Subtotal
                        </p>
                        <p className="text-xl font-semibold text-slate-900">
                          {formatCurrency(
                            estimatedSummary.repairProcessCostTotal || 0,
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-3">
                      <div className="rounded-md border border-white bg-white px-3 py-2 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Washing
                        </p>
                        <p className="mt-1 text-base font-semibold text-slate-900">
                          {formatCurrency(
                            estimatedSummary.washingCostTotal || 0,
                          )}
                        </p>
                      </div>
                      <div className="rounded-md border border-white bg-white px-3 py-2 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Surface Prep
                        </p>
                        <p className="mt-1 text-base font-semibold text-slate-900">
                          {formatCurrency(
                            estimatedSummary.surfacePrepCostTotal || 0,
                          )}
                        </p>
                      </div>
                      <div className="rounded-md border border-white bg-white px-3 py-2 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Material
                        </p>
                        <p className="mt-1 text-base font-semibold text-slate-900">
                          {formatCurrency(
                            estimatedSummary.materialCostTotal || 0,
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      <div className="rounded-lg border border-slate-200 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Team Labor
                        </p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">
                          {formatCurrency(estimatedSummary.teamLaborTotal || 0)}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          General Costs
                        </p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">
                          {formatCurrency(
                            estimatedSummary.generalCostsTotal || 0,
                          )}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Total Mobilization
                        </p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">
                          {formatCurrency(
                            estimatedSummary.estimatedMobilizationCost || 0,
                          )}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Total {areaDataLabels.plural}
                        </p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">
                          {totalElements}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Planned Year
                        </p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">
                          {project.planned_year || "N/A"}
                        </p>
                      </div>
                      <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                          Estimated Total
                        </p>
                        <p className="mt-1 text-lg font-bold text-blue-900">
                          {formatCurrency(displayedEstimatedTotal)}
                        </p>
                      </div>
                    </div>

                    {estimatedSummary.configSummary && (
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                          {estimatedSummary.configSummary.teamName || "Team"}
                        </span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                          {estimatedSummary.configSummary.applicationType ||
                            "N/A"}
                        </span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                          {estimatedSummary.configSummary.coatType || "N/A"}
                        </span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                          {estimatedSummary.configSummary.steelType || "N/A"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {hasActualCostSummary && (
                  <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex flex-col gap-3 border-b border-emerald-200 pb-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                          Actual Cost
                        </p>
                        <p className="text-sm text-emerald-800">
                          Compare recorded completion costs against the
                          estimate.
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                          Actual Total
                        </p>
                        <p className="text-xl font-bold text-emerald-900">
                          {actualCostValue !== null
                            ? formatCurrency(actualCostValue)
                            : "Not recorded"}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-md border border-white bg-white px-3 py-2 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Estimated Total
                        </p>
                        <p className="mt-1 text-base font-semibold text-slate-900">
                          {formatCurrency(displayedEstimatedTotal)}
                        </p>
                      </div>
                      <div className="rounded-md border border-white bg-white px-3 py-2 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Actual Total
                        </p>
                        <p className="mt-1 text-base font-semibold text-slate-900">
                          {actualCostValue !== null
                            ? formatCurrency(actualCostValue)
                            : "Not recorded"}
                        </p>
                      </div>
                      <div className="rounded-md border border-white bg-white px-3 py-2 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Variance
                        </p>
                        <p
                          className={`mt-1 text-base font-semibold ${actualCostVarianceTone}`}
                        >
                          {actualCostVarianceDisplay}
                        </p>
                      </div>
                      <div className="rounded-md border border-white bg-white px-3 py-2 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Completion Date
                        </p>
                        <p className="mt-1 text-base font-semibold text-slate-900">
                          {project.actual_end_date || "Not recorded"}
                        </p>
                      </div>
                    </div>
                    {project.completion_notes && (
                      <div className="mt-3 rounded-md border border-white bg-white px-3 py-3 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Completion Notes
                        </p>
                        <p className="mt-1 text-sm text-slate-900">
                          {project.completion_notes}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {costBreakdownSpacerHeight > 0 && (
            <div
              aria-hidden="true"
              style={{ height: `${costBreakdownSpacerHeight}px` }}
            />
          )}
          {/* Area-Level Repair Details */}
          <div
            ref={repairDetailsSectionRef}
            className="mt-6 bg-white rounded-lg shadow-sm border"
          >
            <div
              className="p-6 cursor-pointer flex items-center justify-between border-b border-gray-200"
              onClick={() => setIsSpansCollapsed(!isSpansCollapsed)}
            >
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={isSpansCollapsed ? faChevronRight : faChevronDown}
                  className="mr-3 text-gray-500"
                />
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {areaDataLabels.plural} & Repair Process Details
                  </h2>
                  <InlineFieldTooltip
                    page="repair_projects"
                    section="repair_process_details"
                    fieldKey="section_header"
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600">
                {spansSearchTerm || spansFilterStatus !== "all" ? (
                  <>
                    <span className="font-medium text-gray-900">
                      {filteredElements.length}
                    </span>{" "}
                    of {totalElements} {getAreaDataCountLabel(totalElements)}
                  </>
                ) : (
                  <>
                    {totalElements} {getAreaDataCountLabel(totalElements)}
                  </>
                )}
              </div>
            </div>

            {!isSpansCollapsed && (
              <div className="p-6">
                {/* Search and Filter Controls */}
                {elements.length > 0 && (
                  <div className="mb-4 flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search by element code, element name, or parent..."
                          value={spansSearchTerm}
                          onChange={(e) => setSpansSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <FontAwesomeIcon
                          icon={faSearch}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        {spansSearchTerm && (
                          <button
                            onClick={() => setSpansSearchTerm("")}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="sm:w-48">
                      <select
                        value={spansFilterStatus}
                        onChange={(e) => setSpansFilterStatus(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All {areaDataLabels.plural}</option>
                        <option value="pending">Pending Only</option>
                        <option value="completed">Completed Only</option>
                      </select>
                    </div>
                    {(spansSearchTerm || spansFilterStatus !== "all") && (
                      <button
                        onClick={() => {
                          setSpansSearchTerm("");
                          setSpansFilterStatus("all");
                        }}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                )}

                {/* Search Results Count */}
                {(spansSearchTerm || spansFilterStatus !== "all") &&
                  elements.length > 0 && (
                    <div className="mb-3 text-sm text-gray-600">
                      {filteredElements.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          No {areaDataLabels.plural.toLowerCase()} match your
                          search criteria. Try adjusting your filters.
                        </div>
                      ) : (
                        <div>
                          Showing{" "}
                          <span className="font-medium text-gray-900">
                            {filteredElements.length}
                          </span>
                          {` ${getAreaDataCountLabel(filteredElements.length)}`}
                          {spansSearchTerm && (
                            <span>
                              {" "}
                              matching "
                              <span className="font-medium text-gray-900">
                                {spansSearchTerm}
                              </span>
                              "
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                {elements.length > 0 ? (
                  <RepairProcessTable
                    spans={elements}
                    onManagePanelBays={handleOpenPanelBayModal}
                    onMarkCompleted={(element) =>
                      handleOpenPanelBayModal(element, "complete")
                    }
                    onMarkUndamaged={(element) =>
                      handleElementCompletion(element, "undamaged")
                    }
                    projectStatus={project.status}
                    projectStarted={hasStarted}
                    completionActionElementId={completionActionElementId}
                    searchTerm={spansSearchTerm}
                    filterStatus={spansFilterStatus}
                    elementLabelSingular={areaDataLabels.singular}
                    elementLabelPlural={areaDataLabels.plural}
                    childLabelSingular={childElementLabels.singular}
                    childLabelPlural={childElementLabels.plural}
                    parentGroupLabelSingular={
                      parentGroupLabels?.singular || "Parent"
                    }
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No {areaDataLabels.plural.toLowerCase()} details available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </>

      {/* Panel Bay Painting Modal */}
      <ProjectCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onComplete={handleProjectCompletion}
        project={project}
        completedSpansCount={completedElements}
        totalSpansCount={totalElements}
        areaDataLabelSingular={areaDataLabels.singular}
        areaDataLabelPlural={areaDataLabels.plural}
      />

      <PanelBayPaintingModal
        isOpen={Boolean(activePanelBaySpan)}
        span={activePanelBaySpan}
        onClose={handleClosePanelBayModal}
        onSave={handlePanelBaySave}
        itemLabelSingular={areaDataLabels.singular}
        childLabelSingular={childElementLabels.singular}
        childLabelPlural={childElementLabels.plural}
        showMarkCompletedButton={
          hasStarted &&
          Boolean(activePanelBaySpan) &&
          !activePanelBaySpan?.isGroupSelection &&
          !Array.isArray(activePanelBaySpan?.items)
        }
        submitButtonLabel={
          panelBayModalMode === "complete" ? "Mark Completed" : "Save Changes"
        }
      />
    </div>
  );
};

export default RepairProjectDetails;
